import { useState, useCallback, type DragEvent } from 'react';
import { log } from '../lib/logger';

interface UseDragDropOptions {
  onFileDrop: (content: string, filename: string) => void;
}

export function useDragDrop({ onFileDrop }: UseDragDropOptions) {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragEnter = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files.length === 0) return;

      const file = files[0];
      if (!file.type.match(/text.*|application\/.*ml/)) {
        log.warn('Unsupported file type', { type: file.type });
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target?.result as string;
        log.info('File loaded', { filename: file.name, length: content.length });
        onFileDrop(content, file.name);
      };
      reader.readAsText(file);
    },
    [onFileDrop]
  );

  return {
    isDragging,
    dragProps: {
      onDragEnter: handleDragEnter,
      onDragLeave: handleDragLeave,
      onDragOver: handleDragOver,
      onDrop: handleDrop,
    },
  };
}
